import styled from "@emotion/styled";
import MessageIcon from "@mui/icons-material/Message";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography
} from "@mui/material";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useMessagesStore } from "features/messages";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTodayEvents } from "../api";

const EventCard = styled(Box)(() => ({
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: "12px"
}));

export const TodayEventPanel = () => {
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const clearRecipients = useMessagesStore(state => state.clearRecipients);
  const addMember = useMessagesStore(state => state.addMember);
  const removeMember = useMessagesStore(state => state.removeMember);
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: todayEvents, isLoading } = useTodayEvents({
    churchId: selectedChurch?.id
  });

  const events = {
    birthdays: todayEvents?.birthdays || [],
    anniversaries: todayEvents?.anniversaries || []
  };

  const sendMessage = member => {
    clearRecipients();
    removeMember(member.id);
    addRecipient(
      `${member?.id}_${member?.first_name}_${member?.last_name}`,
      member
    );
    addMember(member.id, {
      id: member.id
    });
    navigate("/messages");
  };

  return (
    <Stack
      sx={{
        border: "1px solid rgba(255, 255, 255, 0.5)",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(8px)",
        p: 2,
        borderRadius: 6
      }}
      spacing={2}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 1, color: "#fff", fontWeight: 700 }}
      >
        {t("quickboard.todayEvent")}
      </Typography>
      {!isLoading && (
        <Stack
          spacing={2}
          sx={{
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            "&::-webkit-scrollbar": {
              width: "6px"
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255, 255, 255, 0.1)"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255, 255, 255, 0.5)",
              borderRadius: "3px"
            }
          }}
        >
          {events.birthdays?.map((member, index) => (
            <EventCard key={index}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Avatar
                  src={`${IMAGE_URL}/${member.profile_image_url}`}
                  alt={member.first_name}
                >
                  {member.first_name.charAt(0)}
                </Avatar>
                <Stack>
                  <Typography variant="subtitle1">
                    {member.first_name} {member.father_name}
                  </Typography>
                  <Typography variant="caption">
                    {moment().diff(moment(member.date_of_birth), "years")}{" "}
                    {t("quickboard.birthday")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="text"
                  size="small"
                  startIcon={<MessageIcon />}
                  onClick={() => sendMessage(member)}
                >
                  {t("quickboard.sendMessage")}
                </Button>
                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                  {moment(member.date_of_birth).format("DD-MM-YYYY")}
                </Typography>
              </Stack>
            </EventCard>
          ))}

          {events.anniversaries?.map((member, index) => (
            <EventCard key={index}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <AvatarGroup max={2}>
                  <Avatar
                    src={`${IMAGE_URL}/${member.profile_image_url}`}
                    alt={member.first_name}
                  >
                    {member.first_name.charAt(0)}
                  </Avatar>
                  <Avatar
                    src={`${IMAGE_URL}/${member.spouse?.profile_image_url}`}
                    alt={member.spouse?.first_name}
                  >
                    {member.spouse?.first_name.charAt(0)}
                  </Avatar>
                </AvatarGroup>
                <Stack>
                  <Typography variant="subtitle1">
                    {member.first_name} {member.father_name} &{" "}
                    {member.spouse?.first_name} {member.spouse?.father_name}
                  </Typography>
                  <Typography variant="caption">
                    {moment().diff(moment(member.marital_date), "years")}{" "}
                    {t("quickboard.anniversary")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="text"
                  size="small"
                  startIcon={<MessageIcon />}
                  onClick={() => sendMessage(member)}
                >
                  {t("quickboard.sendMessage")}
                </Button>
                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                  {moment(member.date_of_birth).format("DD-MM-YYYY")}
                </Typography>
              </Stack>
            </EventCard>
          ))}

          {events?.birthdays?.length === 0 &&
            events?.anniversaries?.length === 0 && (
              <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                {t("quickboard.noEventsToday")}
              </Typography>
            )}
        </Stack>
      )}

      {isLoading && (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size="24px" sx={{ color: "#fff" }} />
        </Stack>
      )}
    </Stack>
  );
};
